import { useState } from "react";
import { Loader2, Lock, Sparkles, Globe2, MessageCircleQuestion, Mail, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { api, formatApiError } from "../../lib/api";
import ReasonDialog from "../admin/ReasonDialog";
import { ROLE, AUTHOR_CHANNEL, fmtDate } from "../../lib/labels";

const msgLabel = (r) => {
  if (r.is_internal) return "Note interne";
  if (r.author_type !== "entreprise") return "Auteur du feedback";
  if (r.kind === "demande_precision") return "Demande de précision";
  return r.public ? "Réponse publique" : "Réponse privée";
};

export const Message = ({ r }) => {
  const staff = r.author_type === "entreprise";
  const Icon = r.is_internal ? Lock : r.public ? Globe2 : r.kind === "demande_precision" ? MessageCircleQuestion : Mail;
  const tone = r.is_internal ? "border border-dashed border-amber-300 bg-amber-50" : r.public ? "ml-8 border border-emerald-200 bg-emerald-50"
    : staff ? "ml-8 border border-blue-100 bg-blue-50" : "mr-8 bg-slate-100";
  return (
    <div data-testid="conversation-message" className={`rounded-xl p-4 ${tone}`}>
      <p className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-slate-500">
        {staff && <Icon className="h-3 w-3" />}<span data-testid="message-author">{r.author_name}</span>
        {staff && r.author_role && <span className="font-normal">({ROLE[r.author_role]})</span>}
        · <span data-testid="message-kind">{msgLabel(r)}</span> · <time dateTime={r.created_at}>{fmtDate(r.created_at)}</time>
      </p>
      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{r.message}</p>
    </div>
  );
};

const AUDIENCES = [
  ["private", "Réponse privée à l'auteur"], ["public", "Réponse publique (affichée sous l'avis)"],
  ["precision", "Demande de précision"], ["internal", "Note interne (invisible pour l'auteur)"],
];

const bodyFor = (audience, message) => ({
  message, is_internal: audience === "internal", public: audience === "public",
  kind: audience === "precision" ? "demande_precision" : "reponse",
});

export const Composer = ({ fb, onSaved }) => {
  const [msg, setMsg] = useState("");
  const [audience, setAudience] = useState("private");
  const [sending, setSending] = useState(false);
  const closed = !fb.conversation_open;
  const options = AUDIENCES.filter(([k]) => (k !== "public" || fb.can_reply_public) && (!closed || k === "internal"));
  const current = options.some(([k]) => k === audience) ? audience : options[0][0];
  const send = async () => {
    if (msg.trim().length < 2) { toast.error("Le message est trop court"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/company/feedbacks/${fb.id}/responses`, bodyFor(current, msg));
      setMsg(""); onSaved(data); toast.success(current === "internal" ? "Note interne ajoutée" : "Message envoyé à l'auteur");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };
  return (
    <div className="card-surface space-y-3 p-5" data-testid="response-composer">
      {closed && <p className="rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-600">Conversation fermée : seules les notes internes sont possibles. Rouvrez-la pour écrire à l'auteur.</p>}
      <Textarea rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Rédigez votre message…" data-testid="response-input" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <select className="field-select sm:w-80" value={current} onChange={(e) => setAudience(e.target.value)} data-testid="response-audience-select">
          {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="flex gap-2">
          {fb.ai?.suggested_reply && current !== "internal" && <Button variant="outline" size="sm" onClick={() => setMsg(fb.ai.suggested_reply)} data-testid="use-ai-reply-button"><Sparkles className="mr-1.5 h-3.5 w-3.5" />Suggestion IA</Button>}
          <Button size="sm" onClick={send} disabled={sending} data-testid="response-submit-button">{sending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}{current === "internal" ? "Ajouter la note" : "Envoyer"}</Button>
        </div>
      </div>
      {!fb.can_reply_public && fb.visibility === "public" && <p className="text-xs text-slate-400">La réponse publique sera disponible une fois l'avis publié.</p>}
    </div>
  );
};

export const ConversationHeader = ({ fb, can, onSaved }) => {
  const toggle = async () => {
    try { const { data } = await api.post(`/company/feedbacks/${fb.id}/conversation`, { action: fb.conversation_open ? "close" : "reopen" }); onSaved(data); toast.success(fb.conversation_open ? "Conversation fermée" : "Conversation rouverte"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <p className="font-display font-semibold">Conversation</p>
      <span className={`rounded-full px-2 py-0.5 text-xs ${fb.conversation_open ? "bg-emerald-50 text-emerald-700" : "bg-slate-200 text-slate-600"}`} data-testid="conversation-status">{fb.conversation_open ? "Ouverte" : "Fermée"}</span>
      <span className="text-xs text-slate-500" data-testid="author-channels">Auteur joignable via : {fb.author_channels.map((c) => AUTHOR_CHANNEL[c]).join(", ")}</span>
      {can("feedback:respond") && fb.status !== "clos" && <Button size="sm" variant="outline" className="ml-auto" onClick={toggle} data-testid="conversation-toggle-button">{fb.conversation_open ? "Fermer la conversation" : "Rouvrir la conversation"}</Button>}
    </div>
  );
};

export const SensitivePanel = ({ fb, can, onSaved }) => {
  const [asking, setAsking] = useState(false);
  const save = async (sensitive, reason = "") => {
    try { const { data } = await api.post(`/company/feedbacks/${fb.id}/sensitive`, { sensitive, reason }); onSaved(data); setAsking(false); toast.success(sensitive ? "Réclamation marquée sensible" : "Marquage sensible retiré"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className={`card-surface space-y-2 p-5 text-sm ${fb.sensitive ? "border-rose-200 bg-rose-50/50" : ""}`} data-testid="sensitive-panel">
      <p className="flex items-center gap-2 font-medium"><ShieldAlert className={`h-4 w-4 ${fb.sensitive ? "text-rose-600" : "text-slate-400"}`} />{fb.sensitive ? "Réclamation sensible — prioritaire" : "Réclamation standard"}</p>
      <p className="text-xs text-slate-500">{fb.sensitive ? `Accès limité aux propriétaires, responsables et au collaborateur affecté. Jamais publiée.${fb.sensitive_reason ? ` Motif : ${fb.sensitive_reason}` : ""}` : "Marquez ce dossier s'il nécessite une prise en charge prioritaire et confidentielle."}</p>
      {can("feedback:moderate") && (fb.sensitive
        ? <Button size="sm" variant="outline" onClick={() => save(false)} data-testid="sensitive-off-button">Retirer le marquage</Button>
        : <Button size="sm" variant="destructive" onClick={() => setAsking(true)} data-testid="sensitive-on-button">Marquer comme sensible</Button>)}
      {asking && <ReasonDialog testId="sensitive" title="Prise en charge prioritaire" label="Marquer comme sensible" placeholder="Raison (tracée dans l'historique)"
        onConfirm={(r) => save(true, r)} onClose={() => setAsking(false)} />}
    </div>
  );
};
